import { Mesh, MeshStandardMaterial, SphereGeometry, type Vector3 } from 'three';

import type { DebugManager } from '../debug-manager.class';
import type { DebugSphereConfig } from '../types';

export interface AddSphereParams {
	config: DebugSphereConfig;
}

/**
 * Add a debug sphere
 */
export const addSphereCfn = (
	self: DebugManager,
	{ config }: AddSphereParams
): Mesh | null => {
	const { id, position, radius = 1, color = 0xff0000, label } = config;

	// Validate radius - skip if zero, negative, or NaN
	if (!radius || radius <= 0 || !Number.isFinite(radius)) {
		console.warn(`DebugManager.addSphere: Skipping sphere '${id}' with invalid radius: ${radius}`);
		return null;
	}

	// Validate position - skip if any coordinate is NaN
	if (!Number.isFinite(position.x) || !Number.isFinite(position.y) || !Number.isFinite(position.z)) {
		console.warn(`DebugManager.addSphere: Skipping sphere '${id}' with invalid position: [${position.x}, ${position.y}, ${position.z}]`);
		return null;
	}

	// Remove existing sphere with same ID
	if (self.hasSphere(id)) {
		self.removeSphere(id);
	}

	// Create sphere
	const geometry = new SphereGeometry(radius, 16, 16);
	const material = new MeshStandardMaterial({ color });
	const sphere = new Mesh(geometry, material);
	sphere.position.copy(position);
	sphere.name = `debug_sphere_${id}`;
	sphere.visible = self.visible;
	// Mark as interface object to exclude from wireframe mode
	sphere.userData.isInterface = true;
	sphere.userData.type = 'debugVisualization';

	self.scene.add(sphere);
	self.registerSphere(id, sphere);

	// Store label for tooltip
	if (label) {
		self.registerSphereLabel(id, label);
		self.createTooltip(id);
	}

	// Ensure hover detection is enabled if we're visible
	if (self.visible && !self.hoverEnabled) {
		self.enableHoverDetection();
	}

	return sphere;
};

export interface UpdateSphereParams {
	id: string;
	position: Vector3;
}

/**
 * Update a debug sphere position
 */
export const updateSphereCfn = (
	self: DebugManager,
	{ id, position }: UpdateSphereParams
): void => {
	const sphere = self.getSphere(id);
	if (sphere) {
		sphere.position.copy(position);
	}
};

export interface RemoveSphereParams {
	id: string;
}

/**
 * Remove a debug sphere
 */
export const removeSphereCfn = (
	self: DebugManager,
	{ id }: RemoveSphereParams
): void => {
	const sphere = self.getSphere(id);
	if (sphere) {
		self.scene.remove(sphere);
		sphere.geometry.dispose();
		(sphere.material as MeshStandardMaterial).dispose();
		self.unregisterSphere(id);
	}

	// Remove associated tooltip
	self.removeTooltip(id);
	self.unregisterSphereLabel(id);
};
